import React from 'react';
import { makeStyles, useTheme, withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import AddEventForm from './AddEventForm';
import Zoom from '@material-ui/core/Zoom';
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';
import { Snackbar } from '@mui/material';
import MuiAlert from '@material-ui/lab/Alert';
import { useTranslation } from 'react-i18next';

const styles = theme => ({
    root: {
        margin: 0,
        padding: theme.spacing(2),
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
});

const DialogTitle = withStyles(styles)(props => {
    const { children, classes, onClose, ...other } = props;
    return (
        <MuiDialogTitle disableTypography className={classes.root} {...other}>
            <Typography variant="h6">{children}</Typography>
            {onClose ? (
                <IconButton
                    aria-label="close"
                    className={classes.closeButton}
                    onClick={onClose}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    );
});

const DialogContent = withStyles(theme => ({
    root: {
        padding: theme.spacing(2),
    },
}))(MuiDialogContent);

const DialogActions = withStyles(theme => ({
    root: {
        margin: 0,
        padding: theme.spacing(1),
    },
}))(MuiDialogActions);

const useStyles = makeStyles(theme => ({
    fab: {
        position: 'absolute',
        bottom: theme.spacing(3.5),
        left: theme.spacing(5),
        zIndex: '9',
    },
    dialogPaper: {
        width: '92vw',
        maxWidth: 720,
        minWidth: 320,
        margin: theme.spacing(2),
    },
    dialogContent: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        overflowX: 'hidden',
    },
}));

export default function ModalForm(props) {
    const { t } = useTranslation();
    const { onEventCreated } = props;
    const [open, setOpen] = React.useState(false);
    const [formKey, setFormKey] = React.useState(0);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [snackbars, setSnackbars] = React.useState([]);

    const showSnackbar = (message, severity) => {
        const id = Date.now() + Math.random();
        setSnackbars(prev => [...prev, { id, message, severity, open: true }]);
    };

    const handleCloseSnackbar = id => {
        setSnackbars(prev =>
            prev.map(snackbar =>
                snackbar.id === id ? { ...snackbar, open: false } : snackbar
            )
        );
        setTimeout(() => {
            setSnackbars(prev => prev.filter(snackbar => snackbar.id !== id));
        }, 300);
    };

    const resetForm = () => {
        setFormKey(key => key + 1);
    };

    const handleClickOpen = () => {
        resetForm();
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        resetForm();
    };

    const handleEventCreated = newRow => {
        let result = {};
        if (onEventCreated) {
            result = onEventCreated(newRow) || {};
        }

        showSnackbar(
            t('reports.eventsReport.addEvent.success'),
            'success'
        );

        if (result.outsideFilter) {
            showSnackbar(
                t('reports.eventsReport.addEvent.outsideFilter'),
                'warning'
            );
        }

        setOpen(false);
        resetForm();
    };

    const classes = useStyles();
    const theme = useTheme();
    const [value] = React.useState(0);

    const transitionDuration = {
        enter: theme.transitions.duration.enteringScreen,
        exit: theme.transitions.duration.leavingScreen,
    };

    const fabs = [
        {
            color: 'primary',
            className: classes.fab,
            icon: <AddIcon />,
            label: t('reports.eventsReport.addEvent.add'),
        },
    ];

    return (
        <div>
            {fabs.map((fab, index) => (
                <Zoom
                    key={fab.color}
                    in={value === index}
                    timeout={transitionDuration}
                    style={{
                        transitionDelay: `${
                            value === index ? transitionDuration.exit : 0
                        }ms`,
                    }}
                    unmountOnExit
                >
                    <Fab
                        aria-label={fab.label}
                        className={fab.className}
                        color={fab.color}
                        onClick={handleClickOpen}
                    >
                        {fab.icon}
                    </Fab>
                </Zoom>
            ))}
            <Dialog
                onClose={handleClose}
                aria-labelledby="customized-dialog-title"
                open={open}
                fullWidth
                maxWidth={false}
                classes={{ paper: classes.dialogPaper }}
            >
                <DialogTitle id="customized-dialog-title" onClose={handleClose}>
                    {t('reports.eventsReport.addEvent.title')}
                </DialogTitle>
                <DialogContent dividers className={classes.dialogContent}>
                    <AddEventForm
                        formId="add-event-form"
                        hideSubmitButton
                        resetKey={formKey}
                        onSubmittingChange={setIsSubmitting}
                        onEventCreated={handleEventCreated}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="default" disabled={isSubmitting}>
                        {t('reports.eventsReport.addEvent.close')}
                    </Button>
                    <Button
                        type="submit"
                        form="add-event-form"
                        variant="contained"
                        color="primary"
                        autoFocus
                        disabled={isSubmitting}
                    >
                        {isSubmitting
                            ? t('reports.eventsReport.addEvent.adding')
                            : t('reports.eventsReport.addEvent.add')}
                    </Button>
                </DialogActions>
            </Dialog>
            {snackbars.map((snackbar, index) => (
                <Snackbar
                    key={snackbar.id}
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => handleCloseSnackbar(snackbar.id)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    style={{ bottom: 24 + index * 56 }}
                >
                    <MuiAlert
                        elevation={6}
                        variant="filled"
                        severity={snackbar.severity}
                        onClose={() => handleCloseSnackbar(snackbar.id)}
                    >
                        {snackbar.message}
                    </MuiAlert>
                </Snackbar>
            ))}
        </div>
    );
}
